import { useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import {
  Calendar, Clock, MapPin, User, Mail, Phone, CheckCircle, ChevronRight, ChevronLeft, Home, Building2
} from 'lucide-react'

const serviceOptions = [
  { id: 'mowing', label: 'Lawn Mowing' },
  { id: 'fertilization', label: 'Fertilization' },
  { id: 'landscaping', label: 'Landscaping' },
  { id: 'cleanup', label: 'Seasonal Cleanup' },
  { id: 'tree', label: 'Tree & Shrub Care' },
  { id: 'pest', label: 'Pest Control' },
]

const timeSlots = [
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
]

export default function Booking() {
  const ref = useRef()
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    services: [],
    propertyType: '',
    address: '',
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const toggleService = (id) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id]
    }))
  }

  const canProceed = () => {
    switch (step) {
      case 1: return formData.services.length > 0
      case 2: return formData.propertyType && formData.address
      case 3: return formData.date && formData.time && formData.name && formData.email && formData.phone
      default: return true
    }
  }

  const handleSubmit = () => {
    setIsSubmitted(true)
  }

  return (
    <section id="booking" className="section py-24 bg-[#f8faf7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#2d7d32]/10 text-[#2d7d32] rounded-full text-sm font-semibold mb-4">
            <Calendar className="w-4 h-4" />
            Easy Scheduling
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Book Your <span className="gradient-text">Service</span>
          </h2>
        </motion.div>

        {/* Booking Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-6 sm:p-8"
        >
          {/* Progress */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-[#2d7d32] text-white' : 'bg-gray-100 text-gray-400'
                }`}>
                  {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`w-12 sm:w-20 h-0.5 mx-2 ${step > s ? 'bg-[#2d7d32]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {/* Form Content */}
          {isSubmitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[#2d7d32] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Confirmed!</h3>
              <p className="text-gray-600">We'll send a confirmation to {formData.email} shortly.</p>
            </div>
          ) : (
            <>
              {/* Step 1: Services */}
              {step === 1 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Select Services</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {serviceOptions.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          formData.services.includes(service.id)
                            ? 'border-[#2d7d32] bg-[#2d7d32]/5'
                            : 'border-gray-200 hover:border-[#2d7d32]/50'
                        }`}
                      >
                        <span className="font-medium text-gray-900">{service.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Property */}
              {step === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Property Details</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Property Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: 'residential', label: 'Residential', icon: Home },
                        { id: 'commercial', label: 'Commercial', icon: Building2 },
                      ].map((type) => (
                        <button
                          key={type.id}
                          onClick={() => updateForm('propertyType', type.id)}
                          className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                            formData.propertyType === type.id
                              ? 'border-[#2d7d32] bg-[#2d7d32]/5'
                              : 'border-gray-200'
                          }`}
                        >
                          <type.icon className="w-5 h-5" />
                          <span className="font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Address</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => updateForm('address', e.target.value)}
                        placeholder="Enter your address"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2d7d32] outline-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Schedule */}
              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Schedule & Contact</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => updateForm('date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2d7d32] outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">Time</label>
                    <div className="grid grid-cols-3 gap-2">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => updateForm('time', time)}
                          className={`py-2 rounded-lg text-sm font-medium transition-all ${
                            formData.time === time
                              ? 'bg-[#2d7d32] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => updateForm('name', e.target.value)}
                        placeholder="Your name"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2d7d32] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateForm('phone', e.target.value)}
                        placeholder="(904) 555-0123"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2d7d32] outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2d7d32] outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Confirm */}
              {step === 4 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Review</h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Services</span>
                      <span className="font-medium text-right">{formData.services.map(s => serviceOptions.find(o => o.id === s)?.label).join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Property</span>
                      <span className="font-medium">{formData.propertyType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Address</span>
                      <span className="font-medium text-right">{formData.address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date & Time</span>
                      <span className="font-medium">{formData.date} at {formData.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Contact</span>
                      <span className="font-medium text-right">{formData.name}<br />{formData.phone}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
                <button
                  onClick={() => setStep(Math.max(1, step - 1))}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold ${
                    step === 1 ? 'opacity-0 pointer-events-none' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>

                {step < 4 ? (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all ${
                      canProceed()
                        ? 'bg-[#2d7d32] text-white hover:bg-[#1b5e20]'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="flex items-center gap-2 px-8 py-3 bg-[#2d7d32] text-white rounded-full font-semibold hover:bg-[#1b5e20] transition-all"
                  >
                    Confirm Booking
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  )
}
